import { ReactNode, useEffect, useRef, useState } from 'react';
import AlertLineIcon from '../../atoms/icons/AlertLineIcon';
import CloseIcon from '../../atoms/icons/CloseIcon';
import Text, { TextElements } from '../../atoms/typography/Text';
import styles from './Toast.module.css';
import CheckCircleLineIcon from '../../atoms/icons/CheckCircleLineIcon';
import ErrorWarningLineIcon from '../../atoms/icons/ErrorWarningLineIcon';
import InformationLineIcon from '../../atoms/icons/InformationLineIcon';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import { classNames } from '../../utils';
import Notifications from '../../organisms/notifications/Notifications';
import ButtonNew from '../button/Button';
import { TFunctionResult } from 'i18next';

export enum ToastTypes {
    SUCCESS = 'success',
    FAILED = 'failed',
    INFO = 'info',
    WARNING = 'warning',
    FAILED_DARK = 'failed_dark',
}

export interface ToastProps {
    readonly id?: string;
    readonly type: ToastTypes;
    readonly heading?: ReactNode;
    readonly customButton?: ReactNode;
    readonly message?: ReactNode | string | TFunctionResult;
    readonly sticky?: boolean;
}

const getIconByType = (type: ToastTypes) => {
    switch (type) {
        case ToastTypes.SUCCESS:
            return <CheckCircleLineIcon className={styles.iconSuccess} />
        case ToastTypes.FAILED:
            return <ErrorWarningLineIcon className={styles.iconFailed} />
        case ToastTypes.FAILED_DARK:
            return <ErrorWarningLineIcon className={styles.iconFailedDark} />
        case ToastTypes.WARNING:
            return <AlertLineIcon className={styles.iconWarning} />
        case ToastTypes.INFO:
            return <InformationLineIcon className={styles.iconInfo} />
    }
}

const getNotificationClassName = (type: ToastTypes) => {
    switch (type) {
        case ToastTypes.FAILED_DARK:
            return styles.notificationFailedDark;
    }
}

const Toast = ({
    id,
    type,
    heading,
    message,
    customButton,
    sticky = false
}: ToastProps) => {
    const [shouldSlideIn, setShouldSlideIn] = useState<boolean>(true);
    const [shouldSlideOut, setShouldSlideOut] = useState<boolean>(false);

    const timeoutOne = useRef(null);
    const timeoutTwo = useRef(null);

    useEffect(() => {
        timeoutOne.current = setTimeout(() => {
            setShouldSlideIn(false);

            if (!sticky) {
                setShouldSlideOut(true);
            }
        }, 8000);

        timeoutTwo.current = setTimeout(() => {
            if (!sticky) {
                Notifications.close(id);
            }
        }, 8500);
    }, []);

    const closeNotification = () => {
        clearTimeout(timeoutOne.current);
        clearTimeout(timeoutTwo.current);

        setShouldSlideIn(false);
        setShouldSlideOut(true);

        setTimeout(() => {
            Notifications.close(id);
        }, 500);
    }

    return (
        <div className={classNames(styles.Toast, getNotificationClassName(type), shouldSlideIn && styles.slideIn, shouldSlideOut && styles.slideOut)}>
            <div className={styles.content}>
                <div className={styles.iconContainer}>
                    {getIconByType(type)}
                </div>
                <div className={styles.messageContainer}>
                    {heading &&
                        <Text
                            tag='p'
                            weight={FontWeightEnum.Bold}
                            size={FontSizeEnum.Md}
                            className={styles.heading}
                        >
                            {heading}
                        </Text>
                    }
                    {message &&
                        <Text
                            tag='p'
                            size={heading && FontSizeEnum.Sm}
                            weight={!heading && FontWeightEnum.Regular}
                            color={!heading && ColorEnum.Gray800}
                            className={styles.message}
                        >
                            {message as TextElements}
                        </Text>
                    }
                </div>
                <div className={styles.closeButtonContainer}>
                    <ButtonNew variant='empty' onClick={closeNotification}>
                        <CloseIcon />
                    </ButtonNew>
                </div>
            </div>
            {customButton &&
                <div className={styles.customButton}>
                    {customButton}
                </div>
            }
        </div>
    );
}

export default Toast;
